import { BehaviorSubject } from "rxjs";
import { ModalDefinition } from "@modules/+new/shared/universal-modal/entities/modal-definition.interface";
import { ModalNode, ModalNodeDefinition } from "@modules/+new/shared/universal-modal/entities/modal-node.class";

/***
 * @description List should only hold list of modal definitions, nothing else.
 * It exposes lastDefinition$ subject and few methods to update it's state.
 */
export class ModalList {
	private debug: boolean = false;
	private head: ModalNode;
	private getTail = (node: ModalNode): ModalNode => (node.next ? this.getTail(node.next) : node);
	private getFromTail = (node: ModalNode = this.last, count = 1): ModalNode =>
		node.prev && count ? this.getFromTail(node.prev, count - 1) : node;

	private setHead = (node: ModalNode): ModalNode => {
		this.head = node;
		this.updateLastNodeDefinition(node);
		return node;
	};
	private updateLastNodeDefinition = (node: ModalNode): void => {
		if (!node) {
			this.lastDefinition$.next(null);
			return;
		}
		this.lastDefinition$.next(node.toObject());
		this.debug && this.printDebug();
	};

	public get last(): ModalNode {
		return this.getTail(this.head);
	}
	public get prev(): ModalNode {
		return this.last?.prev;
	}

	public lastDefinition$: BehaviorSubject<ModalNodeDefinition> = new BehaviorSubject(null);

	public toArray: () => ModalNode[] = () => {
		const array: ModalNode[] = [];
		if (!this.head) {
			return [];
		}
		const next = (node: ModalNode): ModalNode[] => {
			array.push(node);
			return node.next ? next(node.next) : array;
		};

		return next(this.head);
	};

	public flush = (): void => {
		this.head = null;
		this.updateLastNodeDefinition(null);
	};

	public add(def: ModalDefinition): ModalNode {
		const node = new ModalNode(def);
		if (!this.head) {
			return this.setHead(node);
		}

		const tail: ModalNode = this.last;
		node.prev = tail;
		tail.next = node;
		this.updateLastNodeDefinition(node);
		return node;
	}

	public revert(stepsBack = 1): ModalNode {
		if (!this.head?.next) {
			return;
		}

		const prev: ModalNode = this.getFromTail(this.last, stepsBack);
		prev.next = null;
		this.updateLastNodeDefinition(prev);
		return prev;
	}

	private printDebug = (): void => {
		const arr: ModalNode[] = this.toArray();
		const currentStack = arr.map((item) => ({
			component: item.definition.component.name,
			url: item.definition.url,
			index: item.index,
			hasPrev: !!item.prev,
			hasNext: !!item.next
		}));

		console.debug("current head", this.head);
		console.debug("current stack", arr);
		console.table(currentStack);
		console.debug("current def", this.lastDefinition$.getValue());
	};
}
